import { useEffect } from "react";

export interface KeyboardShortcut {
    key: string;
    modifiers: ("shift" | "ctrl" | "alt" | "meta")[];
    action: () => void;
    description?: string;
}

/**
 * Check if a keyboard event matches a shortcut definition
 */
function matchesShortcut(event: KeyboardEvent, shortcut: KeyboardShortcut): boolean {
    // Check if the key matches
    if (event.key.toLowerCase() !== shortcut.key.toLowerCase()) {
        return false;
    }

    // Check if all required modifiers are pressed
    const eventModifiers = {
        shift: event.shiftKey,
        ctrl: event.ctrlKey || event.metaKey, // Support both Ctrl and Cmd
        alt: event.altKey,
        meta: event.metaKey,
    };

    // Ensure all required modifiers are pressed
    for (const modifier of shortcut.modifiers) {
        if (!eventModifiers[modifier]) {
            return false;
        }
    }

    // Ensure no extra modifiers are pressed (except meta/ctrl equivalence)
    const requiredModifiers = new Set(shortcut.modifiers);
    if (eventModifiers.shift && !requiredModifiers.has("shift")) return false;
    if (eventModifiers.alt && !requiredModifiers.has("alt")) return false;

    // Handle ctrl/meta equivalence
    const hasCtrlOrMeta = requiredModifiers.has("ctrl") || requiredModifiers.has("meta");
    if ((eventModifiers.ctrl || eventModifiers.meta) && !hasCtrlOrMeta) return false;

    return true;
}

/**
 * Global keyboard shortcut handler
 * Supports multiple shortcuts with flexible key combinations
 */
export function useGlobalKeyboard(shortcuts: KeyboardShortcut[]) {
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Skip if user is typing in an input field
            const target = event.target as HTMLElement;
            if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
                return;
            }

            // Check each shortcut
            for (const shortcut of shortcuts) {
                if (matchesShortcut(event, shortcut)) {
                    event.preventDefault();
                    shortcut.action();
                    console.log(`Shortcut triggered: ${shortcut.description || shortcut.key}`);
                    break; // Only trigger the first matching shortcut
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [shortcuts]);
}
